import { useEffect, useState, useMemo } from "react";
import { DataCard, Button, Spinner } from "mueller-controls";
import "./index.scss";
import { DateRange, Phone, Info, PhoneCallback } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { getAppointmentInformation, postAppointmentSelection, AutomaticAppointmentDtoV2, BookingSiteSubmitResponse, AppointmentAssignmentCandidate } from "requests/automaticAppointmentsV2";
import { AutomaticAppointmentStatus } from "requests/automaticAppointment";

enum Page {
  Summary,
  Booking,
  CallMe,
  Expired,
  Confirm,
  NoCandidates,
  Error,
};

const NOT_LOADING = "not-loading";
const POSTING_CALL = "posting-call";
const POSTING_CONFIRM = "posting-confirm";
const FETCHING_DATA = "fetching-data"
type LoadingState = typeof NOT_LOADING | typeof POSTING_CALL | typeof POSTING_CONFIRM | typeof FETCHING_DATA;

const BOOKING_INTRO_MESSAGE = "Select your preferred date & time below, then click confirm:"
const BOOKING_NOT_VALID = "We're sorry, but the time selected is no longer available. Please select one of the remaining times:"
const BOOKING_REFRESHED = "We're sorry, but the time selected is no longer available. Options have been refreshed:"
type BookingMessage = typeof BOOKING_INTRO_MESSAGE | typeof BOOKING_NOT_VALID | typeof BOOKING_REFRESHED;

const getFormattedFromDateString = (date: string) => {
  return new Date(date).toLocaleDateString(
    "en-US",
    {
      weekday: "long",
      year: "2-digit",
      month: "numeric",
      day: "numeric",
    }
  );
};

const getTimeSlotFromDate = (date: Date) => date.getHours() < 13 ? "8am to 1pm" : "1pm to 6pm";
const getTimeSlotFromDateString = (date: string) => getTimeSlotFromDate(new Date(date));

const getAgentLabel = (data: AutomaticAppointmentDtoV2) => data.appointmentInformation.isAppraisal ? "Broker" : "Agent";

const PolicyHolderBooking = () => {
  const [page, setPage] = useState(Page.Summary);
  const [data, setData] = useState<AutomaticAppointmentDtoV2>();
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(null);
  const [loadingState, setLoadingState] = useState<LoadingState>(FETCHING_DATA);
  const [invalidIndexes, setInvalidIndexes] = useState<number[]>([]);
  const [bookingMessage, setBookingMessage] = useState<BookingMessage>(BOOKING_INTRO_MESSAGE);

  let params = useParams();
  const getData = () => {
    setLoadingState(FETCHING_DATA);
    getAppointmentInformation(params.workItemId, params.campaign, params.routeKey, (data: AutomaticAppointmentDtoV2) => {
      if (!data) return;
      setData(data);
      
      // show the page expired when the has as an appt already
      if (data.appointmentInformation.hasAppointmentSet) {
          setPage(Page.Expired)
      }
      else{
          switch (data.appointmentInformation.appointmentStatus) {
              case AutomaticAppointmentStatus.Completed_DateChosen:
                  setPage(Page.Confirm);
                  break;
              case AutomaticAppointmentStatus.Completed_CallMe:
                  setPage(Page.CallMe);
                  break;
              case AutomaticAppointmentStatus.Canceled_Timeout:
              case AutomaticAppointmentStatus.Canceled_StatusChange:
                  setPage(Page.Expired);
                  break;
              default:
                  if (!data?.appointmentCandidates?.candidates?.length) {
                      setPage(Page.NoCandidates)
                  }
                  break;
          }
      }

      setLoadingState(NOT_LOADING);
    });
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const contactUsPhoneNumber = useMemo(() => {
    // Fall back to hard coded CS number if we don't have a case config one
    return data?.appointmentInformation?.muellerPhoneNumber ?? "1-800-565-6063";  
  }, [data]);

  const onConfirmClicked = () => {
    setLoadingState(POSTING_CONFIRM);
    const candidate = data?.appointmentCandidates?.candidates[selectedIndex];
    const submissionCandidate = {
      fieldRepNumber: candidate.fieldRepNumber,
      appointmentDateTime: candidate.appointmentDateTime,
      confirmationMessageRecipients: [""] // This is not used for policyholder appt booking
    }
    const handleResults = (data?: BookingSiteSubmitResponse) => {
      if (data?.success) {
        setPage(Page.Confirm);
        setLoadingState(NOT_LOADING);
        return;
      }
      if (data?.invalidateSelectedOnly) {
        setInvalidIndexes([...invalidIndexes, selectedIndex]);
        setSelectedIndex(null);
        setBookingMessage(BOOKING_NOT_VALID);
        setLoadingState(NOT_LOADING);
        return;
      }
      if (data?.needsRefresh) {
        setSelectedIndex(null);
        setInvalidIndexes([]);
        setBookingMessage(BOOKING_REFRESHED);
        getData();
      }
    };
    const handleError = (data?: any) => setPage(Page.Error);  // TODO: Display message?
    postAppointmentSelection(params.workItemId, params.campaign, params.routeKey, { callMeRequested: false, candidate: submissionCandidate }, handleResults, handleError);
  };

  const onRequestCallbackClicked = () => {
    setLoadingState(POSTING_CALL);
    const handleResults = (data?: any) => setPage(Page.CallMe);
    const handleError = (data?: any) =>  setPage(Page.Error);  // TODO: Display message?
    postAppointmentSelection(params.workItemId, params.campaign, params.routeKey, { callMeRequested: true, candidate: null }, handleResults, handleError);
  };
  const onContactUsClicked = () => {
    const a = document.createElement("a");
    a.href = `tel:+${contactUsPhoneNumber}`;
    a.click();
  };
  const onAboutClicked = () => window.location.href = "https://www2.muellerreports.com";
  const onBookClicked = () => data?.appointmentCandidates?.candidates?.length 
    ? setPage(Page.Booking) 
    : setPage(Page.NoCandidates);

  const setPostingCall = () => setLoadingState(POSTING_CALL);

  const getComponentForPage = (page: Page) => {
    switch (page) {
      case Page.Summary:
        return (<SummaryPage
          data={data}
          onBookClicked={onBookClicked}
          onRequestCallbackClicked={onRequestCallbackClicked}
          onAboutClicked={onAboutClicked}
          onContactUsClicked={onContactUsClicked}
          isPostingCallMe={loadingState === POSTING_CALL}
          setIsPostingCallMe={setPostingCall}
          contactUsPhoneNumber={contactUsPhoneNumber}
        />);
      case Page.Booking:
        return (
          <BookingPage 
            candidates={data?.appointmentCandidates?.candidates} 
            isWithinCallCenterHours={data?.appointmentInformation?.isWithinCallCenterHours ?? false}
            isCallCenterEligible={data?.appointmentInformation?.isCallCenterEligible ?? false}
            onConfirmClicked={onConfirmClicked} 
            onRequestCallbackClicked={onRequestCallbackClicked} 
            onContactUsClicked={onContactUsClicked} 
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
            invalidIndexes={invalidIndexes}
            loadingState={loadingState}
            setLoadingState={setLoadingState}
            bookingMessage={bookingMessage}
          />
        );
      case Page.CallMe:
        return (<CallMePage data={data} onContactUsClicked={onContactUsClicked} onAboutClicked={onAboutClicked} />);
      case Page.Expired:
        return (<ExpiredPage data={data} onContactUsClicked={onContactUsClicked} onAboutClicked={onAboutClicked} />);
      case Page.Confirm:
        return (<ConfirmationPage data={data} selectedIndex={selectedIndex} contactUsPhoneNumber={contactUsPhoneNumber} />);
      case Page.NoCandidates:
        return (<NoCandidatesPage data={data} onContactUsClicked={onContactUsClicked} onAboutClicked={onAboutClicked} />);
      case Page.Error:
        return (<ErrorPage />);
    }
  };

  return (
    <div className="policy-holder-booking">
      <div className="logos">
        <img src="//www2.muellerreports.com/wp-content/uploads/2016/11/Mueller-logo-web.png" alt="Mueller Logo" />
        {!logoLoadFailed && data?.appointmentInformation?.logoUrl && <img src={data.appointmentInformation.logoUrl} alt="Customer Logo" onError={() => setLogoLoadFailed(true)} /> /* we dont know if the image exists or not, the server sends back a url either way, so we will just try to load it */}
      </div>
      {getComponentForPage(page)}
    </div>
  );
};

interface SummaryProps {
  data: AutomaticAppointmentDtoV2;
  onBookClicked: () => void;
  onRequestCallbackClicked: () => void;
  onAboutClicked: () => void;
  onContactUsClicked: () => void;
  isPostingCallMe: boolean;
  setIsPostingCallMe: () => void;
  contactUsPhoneNumber: string;
};

const SummaryPage = (props: SummaryProps) => {
  const onRequestCallbackClickedWrapped = () => {
    props.setIsPostingCallMe();
    props.onRequestCallbackClicked();
  };

  return (
    <>
      <DataCard
        title={props.data?.appointmentInformation.policyHolderName ?? "Loading..."}
        isLoading={!props.data}
        data={props.data}
        fields={[
          { label: "Address", property: "appointmentInformation.address" },
          { label: "Company", property: "appointmentInformation.company" },
          props.data?.appointmentInformation?.agent && { label: getAgentLabel(props.data), property: "appointmentInformation.agent" },
        ]}
      />
      {
        (!props.data || (!props.data?.appointmentInformation?.isWithinCallCenterHours)) &&
        (
          <p>
            {`The Mueller scheduling department is available at ${props.contactUsPhoneNumber} to answer any of your questions Monday through Friday 8am-7pm EST and Sat 8am-4pm EST.`}
            <br />
            <br />
            Alternately, you may request a callback or book your appointment via the appropriate button below.
          </p>
        )
      }
      <Button disabled={props.isPostingCallMe || !props.data} startIcon={<DateRange />} onClick={props.onBookClicked}>Book Appointment</Button>
      {
        
        props.data?.appointmentInformation?.isCallCenterEligible && 
        <Button disabled={props.isPostingCallMe || !props.data} isLoading={props.isPostingCallMe} startIcon={<PhoneCallback />} onClick={onRequestCallbackClickedWrapped}>Request Callback</Button>}
      {
        (!props.data && <Button disabled={true} startIcon={<Phone />}>Contact Us</Button>)
        ||
        (
          props.data?.appointmentInformation?.isWithinCallCenterHours &&
          <Button disabled={props.isPostingCallMe || !props.data} startIcon={<Phone />} onClick={props.onContactUsClicked}>Contact Us</Button>
        )
      }      
      <Button disabled={props.isPostingCallMe || !props.data} startIcon={<Info />} onClick={props.onAboutClicked}>About Mueller</Button>
    </>
  );
};

interface BookingProps {
  candidates: AppointmentAssignmentCandidate[];
  isWithinCallCenterHours: boolean;
  isCallCenterEligible: boolean;
  onConfirmClicked: () => void;
  onRequestCallbackClicked: () => void;
  onContactUsClicked: () => void;
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  invalidIndexes: number[];
  loadingState: string;
  setLoadingState: (loadingState: LoadingState) => void;
  bookingMessage: BookingMessage;
};

const BookingPage = (props: BookingProps) => {
  const getCandidateButtonLabel = (dateString: string) => {
    const date = new Date(dateString);
    const dateLabel = date.toLocaleDateString(
      "en-US",
      {
        weekday: "long",
        month: "numeric",
        day: "numeric"
      }
    );
    var timeSlot = getTimeSlotFromDate(date);
    return `${dateLabel} ${timeSlot}`;
  };

  if (!props?.candidates?.length) return null;

  return <>
    <p className="centered display-text">{props.bookingMessage}</p>
    {props.loadingState === FETCHING_DATA && <Spinner />}
    {props.loadingState !== FETCHING_DATA && props.candidates.map((c, i) => {
      return (
        <Button
          key={`${c.appointmentDateTime}-${i}`}
          disabled={props.loadingState !== NOT_LOADING || props.invalidIndexes.includes(i)}
          onClick={() => props.setSelectedIndex(i)}
          variant={props.selectedIndex === i ? "contained" : "outlined"}
        >
            {getCandidateButtonLabel(c.appointmentDateTime)}
        </Button>
      );
    })}
    <br />
    <Button
      disabled={props.selectedIndex === null || props.loadingState !== NOT_LOADING}
      isLoading={props.loadingState === POSTING_CONFIRM}
      onClick={props.onConfirmClicked}
    >
        Confirm
    </Button>
    <br />
    <p className="centered display-text">If the options above do not fit your schedule, choose an option below:</p>
    {
      props.isCallCenterEligible && 
      <Button
      disabled={props.loadingState !== NOT_LOADING}
      isLoading={props.loadingState === POSTING_CALL}
      startIcon={<PhoneCallback />}
      onClick={props.onRequestCallbackClicked}
    >
        Request Callback Instead
    </Button>
    }
    {
      props.isWithinCallCenterHours &&
      <Button
        disabled={props.loadingState !== NOT_LOADING}
        isLoading={props.loadingState === POSTING_CALL}
        startIcon={<Phone />}
        onClick={props.onContactUsClicked}
      >
          Contact Us Instead
      </Button>
    }
  </>;
};

interface CallMePageProps {
  data: AutomaticAppointmentDtoV2,
  onContactUsClicked: () => void;
  onAboutClicked: () => void;
}

const CallMePage = (props: CallMePageProps) =>
  <>
    <div className="call-me">
      <h1 className="centered">We'll be in touch...</h1>
      <div className="display-text">
        You will be contacted by a member of our team to schedule an appointment. If the available dates didn't work for you, we'll review what other accommodations might be available.
        <br />
        <br />
        If you prefer, click contact us to speak with someone now.
      </div>
      <br />
    </div>
    {
      props.data?.appointmentInformation?.isWithinCallCenterHours &&
      <Button
        startIcon={<Phone />}
        onClick={props.onContactUsClicked}
      >
          Contact Us
      </Button>
    }
    <Button 
      startIcon={<Info />} 
      onClick={props.onAboutClicked}>
        About Mueller
    </Button>
  </>;

interface ExpiredPageProps {
  data: AutomaticAppointmentDtoV2,
  onContactUsClicked: () => void;
  onAboutClicked: () => void;
}

const ExpiredPage = (props: ExpiredPageProps) =>
    <>
        <div className="call-me">
            <h1 className="centered">Appointment Link Has Expired</h1>
            <div className="display-text">
                If you require assistance, please tap the contact us button below.
            </div>
        </div>
        <Button
            startIcon={<Phone />}
            onClick={props.onContactUsClicked}>
            Contact Us
        </Button>
        <Button
            startIcon={<Info />}
            onClick={props.onAboutClicked}>
            About Mueller
        </Button>
    </>;

interface ConfirmationProps {
  data: AutomaticAppointmentDtoV2,
  selectedIndex: number;
  contactUsPhoneNumber: string;
};

const ConfirmationPage = (props: ConfirmationProps) =>
  <div className="confirmation">
    <h1 className="centered">Your request for an appointment is awaiting confirmation!</h1>
    <DataCard
      title={props.data?.appointmentInformation.policyHolderName ?? "Loading..."}
      data={props.data}
      fields={[
        { label: "Address", property: "appointmentInformation.address" },
        { label: "Company", property: "appointmentInformation.company" },
        props.data?.appointmentInformation?.agent && { label: getAgentLabel(props.data), property: "appointmentInformation.agent" },
        Number.isInteger(props.selectedIndex) && { label: "Date", value: getFormattedFromDateString(props.data?.appointmentCandidates?.candidates[props.selectedIndex]?.appointmentDateTime) },
        Number.isInteger(props.selectedIndex) && { label: "Time Window", value: getTimeSlotFromDateString(props.data?.appointmentCandidates?.candidates[props.selectedIndex]?.appointmentDateTime) },
      ]}
    />
    <div className="display-text">
      A notification will be sent shortly confirming your approximate appointment time.
      If you have any questions, contact your agent, or our customer service department at  (<a href={`tel:${props.contactUsPhoneNumber}`}>{`${props.contactUsPhoneNumber}`}</a>).
    </div>
  </div>;

interface NoCandidatesPageProps {
  data: AutomaticAppointmentDtoV2,
  onContactUsClicked: () => void;
  onAboutClicked: () => void;
}

const NoCandidatesPage = (props: NoCandidatesPageProps) => 
  <>
    <div className="displayText">
      Our automated system was unable to locate an available appointment time. 
    </div>
    <div className="displayText">
      You will be contacted by a member of our team to schedule an appointment. If you prefer, click contact us to speak with someone now.
    </div>
    <br />
    {
      props.data?.appointmentInformation?.isWithinCallCenterHours &&
      <Button
        startIcon={<Phone />}
        onClick={props.onContactUsClicked}
      >
          Contact Us
      </Button>
    }
    <Button 
      startIcon={<Info />} 
      onClick={props.onAboutClicked}>
        About Mueller
    </Button>
  </>;

const ErrorPage = () => 
  <div className="call-me">
    <h1 className="centered">An Error Has Occured</h1>
    <div className="centered display-text">Please try again later</div>
  </div>;

export default PolicyHolderBooking;
